/**
 * AdminPeople - Unified People Directory
 * Single source of truth for all personnel (coaches, staff, board members)
 * With embedded add/edit forms and bulk actions
 */
import "../styles/people-families.css";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import PeopleWorkspaceNav from "../components/people/PeopleWorkspaceNav";
import AdminRegistrations from "./AdminRegistrations";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Users, UserPlus, Search, Loader2,
  GraduationCap, Briefcase, Crown, AlertCircle, CheckCircle,
  Clock, Shield, Mail, Phone, Calendar,
  Eye, Send, User, X, AlertTriangle,
  Download, Trash2, MoreHorizontal, RefreshCw,
  UserCheck, UserX, Home
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Import the existing dialog components
import {
  StaffDialog,
  BoardMemberDialog,
  STAFF_ROLES,
  BOARD_POSITIONS,
  BOARD_PERMISSIONS
} from "../components/team-staff";

// Type configurations
const TYPE_CONFIG = {
  player: {
    label: "Player",
    icon: User,
    color: "bg-slate-100 text-slate-700",
    bgLight: "bg-cyan-50"
  },
  parent: {
    label: "Parent",
    icon: Users,
    color: "bg-slate-100 text-slate-700",
    bgLight: "bg-violet-50"
  },
  household: {
    label: "Household",
    icon: Home,
    color: "bg-slate-100 text-slate-700",
    bgLight: "bg-teal-50"
  },
  coach: { 
    label: "Coach", 
    icon: GraduationCap, 
    color: "bg-slate-100 text-slate-700",
    bgLight: "bg-blue-50"
  },
  staff: { 
    label: "Staff", 
    icon: Briefcase, 
    color: "bg-slate-100 text-slate-700",
    bgLight: "bg-emerald-50"
  },
  board: { 
    label: "Board", 
    icon: Crown, 
    color: "bg-slate-100 text-slate-700",
    bgLight: "bg-purple-50"
  }
};

const STATUS_CONFIG = {
  active: { label: "Active", color: "bg-green-100 text-green-700", icon: CheckCircle },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  issues: { label: "Issues", color: "bg-red-100 text-red-700", icon: AlertTriangle },
  inactive: { label: "Inactive", color: "bg-slate-100 text-slate-500", icon: X }
};

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminPeopleDirectory = ({ embedded = false, routeBase = "/admin/people" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [people, setPeople] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [summary, setSummary] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(searchParams.get("type") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Profile panel state
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileTab, setProfileTab] = useState("overview");
  
  // Dialog states
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [staffForm, setStaffForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", role: "manager", permissions: "limited", assigned_programs: []
  });
  const [boardForm, setBoardForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", position: "member_at_large", 
    title: "", bio: "", term_start: "", term_end: "", linkedin_url: "", show_on_public_page: true,
    has_system_access: false, send_invite: false,
    permissions: {
      view_financials: false, view_registrations: false, view_programs: false,
      approve_coaches: false, manage_staff: false, manage_settings: false, full_admin: false
    }
  });
  const [addPersonForm, setAddPersonForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", role: "coach"
  });
  const [editMode, setEditMode] = useState(false);
  const [editItem, setEditItem] = useState(null);
  
  // Debug state (shows when no people found)
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch people data
  const fetchPeople = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);

      const householdRequest = axios.get(`${API}/admin/households`, {
        headers,
        params: { ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}), page: 1, page_size: 100 },
      });
      const [response, householdResponse] = await Promise.all([
        axios.get(`${API}/people?${params.toString()}`, { headers }),
        householdRequest,
      ]);
      const households = householdResponse.data.items || (Array.isArray(householdResponse.data) ? householdResponse.data : []);
      const familyDirectory = households.flatMap((household) => [
        {
          person_id: household.household_id,
          person_type: "household",
          first_name: household.name || household.primary_contact_name || "Household",
          last_name: "",
          email: household.primary_contact_email || "",
          phone: household.primary_contact_phone || "",
          status: "active",
          household_id: household.household_id,
          household_name: household.name,
        },
        ...(household.players || []).map((player) => ({
          ...player,
          person_id: player.player_id,
          person_type: "player",
          status: player.status || "active",
          household_id: household.household_id,
          household_name: household.name,
        })),
        ...(household.members || []).map((member) => ({
          ...member,
          person_id: member.member_id || member.user_id,
          person_type: "parent",
          status: member.status || "active",
          household_id: household.household_id,
          household_name: household.name,
        })),
      ]);
      const directoryPeople = [...(response.data.people || []), ...familyDirectory];
      const filteredPeople = directoryPeople.filter((person) => {
        const matchesView = activeFilter === "all"
          || (activeFilter === "staff" && ["coach", "staff", "board"].includes(person.person_type))
          || person.person_type === activeFilter;
        return matchesView && (statusFilter === "all" || person.status === statusFilter);
      });
      setPeople(filteredPeople);
      setPendingInvites(response.data.pending_invites || []);
      setSummary({
        total: directoryPeople.length,
        players: directoryPeople.filter((person) => person.person_type === "player").length,
        parents: directoryPeople.filter((person) => person.person_type === "parent").length,
        households: directoryPeople.filter((person) => person.person_type === "household").length,
        staff: directoryPeople.filter((person) => ["coach", "staff", "board"].includes(person.person_type)).length,
        pending: directoryPeople.filter((person) => person.status === "pending").length,
        issues: directoryPeople.filter((person) => person.status === "issues").length,
      });
      
      // If no people found, fetch debug info
      if ((response.data.people || []).length === 0) {
        fetchDebugInfo();
      }
    } catch (error) {
      console.error("Error fetching people:", error);
      setLoadError(true);
      toast.error("Failed to load people directory");
    } finally {
      setLoading(false);
    }
  };

  // Fetch debug info when no people found
  const fetchDebugInfo = async () => {
    try {
      const response = await axios.get(`${API}/people/debug/org-data`, { headers });
      setDebugInfo(response.data);
    } catch (error) {
      console.error("Error fetching debug info:", error);
      // Try alternative debug method
      try {
        // Decode token to get user info
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          setDebugInfo({
            user_info: {
              email: payload.email,
              role: payload.role,
              org_id: payload.org_id
            },
            error: "Debug endpoint not available - showing token info"
          });
        }
      } catch (e) {
        console.error("Could not decode token:", e);
      }
    }
  };

  // Fetch person profile
  const fetchProfile = async (personType, personId) => {
    try {
      setProfileLoading(true);
      const response = await axios.get(`${API}/people/${personType}/${personId}`, { headers });
      setProfileData(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    fetchPeople();
  }, [activeFilter, statusFilter]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchPeople();
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    if (!staffDialogOpen) {
      setStaffForm({ first_name: "", last_name: "", email: "", phone: "", role: "manager", permissions: "limited", assigned_programs: [] });
      setEditMode(false);
      setEditItem(null);
    }
  }, [staffDialogOpen]);

  useEffect(() => {
    if (!boardDialogOpen) {
      setBoardForm({
        first_name: "", last_name: "", email: "", phone: "", position: "member_at_large", 
        title: "", bio: "", term_start: "", term_end: "", linkedin_url: "", show_on_public_page: true,
        has_system_access: false, send_invite: false,
        permissions: { view_financials: false, view_registrations: false, view_programs: false,
          approve_coaches: false, manage_staff: false, manage_settings: false, full_admin: false }
      });
      setEditMode(false);
      setEditItem(null);
    }
  }, [boardDialogOpen]);

  useEffect(() => {
    if (!addPersonOpen) {
      setAddPersonForm({ first_name: "", last_name: "", email: "", phone: "", role: "coach" });
    }
  }, [addPersonOpen]);

  const handleAddPerson = async () => {
    if (!addPersonForm.first_name.trim() || !addPersonForm.last_name.trim() || !addPersonForm.email.trim()) {
      toast.error("Add a first name, last name, and email");
      return;
    }
    setSaving(true);
    try {
      const common = {
        first_name: addPersonForm.first_name.trim(),
        last_name: addPersonForm.last_name.trim(),
        email: addPersonForm.email.trim(),
        phone: addPersonForm.phone.trim(),
      };
      if (addPersonForm.role === "coach") {
        await axios.post(`${API}/coaches/invite`, { ...common, org_id: user?.org_id }, { headers });
      } else if (addPersonForm.role === "board") {
        await axios.post(`${API}/admin/board-members`, { ...common, position: "member_at_large" }, { headers });
      } else {
        await axios.post(`${API}/admin/staff`, { ...common, role: addPersonForm.role, permissions: "limited" }, { headers });
      }
      toast.success("Person added and invitation queued");
      setAddPersonOpen(false);
      fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add person");
    } finally {
      setSaving(false);
    }
  };

  // Handle resend coach invite
  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/coaches/invite/${inviteId}/resend`, {}, { headers });
      toast.success("Invite resent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invite");
    }
  };

  // Handle cancel coach invite
  const handleCancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/coaches/invite/${inviteId}`, { headers });
      toast.success("Invite cancelled");
      fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel invite");
    }
  };

  // Handle save staff
  const handleSaveStaff = async () => {
    if (!staffForm.first_name || !staffForm.last_name || !staffForm.email) {
      toast.error("Please fill in name and email");
      return;
    }
    setSaving(true);
    try {
      if (editMode && editItem) {
        await axios.put(`${API}/admin/staff/${editItem.person_id}`, staffForm, { headers });
        toast.success("Staff member updated");
      } else {
        await axios.post(`${API}/admin/staff`, staffForm, { headers });
        toast.success("Staff member added");
      }
      setStaffDialogOpen(false);
      fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save staff member");
    } finally {
      setSaving(false);
    }
  };

  // Handle save board member
  const handleSaveBoard = async () => {
    if (!boardForm.first_name || !boardForm.last_name) {
      toast.error("Please fill in name");
      return;
    }
    setSaving(true);
    try {
      if (editMode && editItem) {
        await axios.put(`${API}/admin/board-members/${editItem.person_id}`, boardForm, { headers });
        toast.success("Board member updated");
      } else {
        await axios.post(`${API}/admin/board-members`, boardForm, { headers });
        toast.success("Board member added");
      }
      setBoardDialogOpen(false);
      fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save board member");
    } finally {
      setSaving(false);
    }
  };

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedIds.length === people.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(people.map(p => `${p.person_type}-${p.person_id}`));
    }
  };

  const handleSelectPerson = (person) => {
    const id = `${person.person_type}-${person.person_id}`;
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkExport = async () => {
    setBulkActionLoading(true);
    try {
      const selectedPeople = people.filter(p => selectedIds.includes(`${p.person_type}-${p.person_id}`));
      
      // Create CSV content
      const headers = ["Type", "First Name", "Last Name", "Email", "Phone", "Status"];
      const rows = selectedPeople.map(p => [
        p.person_type,
        p.first_name || "",
        p.last_name || "",
        p.email || "",
        p.phone || "",
        p.status || ""
      ]);
      
      const csvContent = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `people-export-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedPeople.length} people`);
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to export");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkSendInvites = async () => {
    setBulkActionLoading(true);
    try {
      const selectedPeople = people.filter(p => 
        selectedIds.includes(`${p.person_type}-${p.person_id}`) && p.status === "pending"
      );
      
      let sent = 0;
      for (const person of selectedPeople) {
        try {
          if (person.person_type === "coach") {
            // Resend coach invite would go here
            sent++;
          }
        } catch (e) {
          console.error("Failed to send invite to", person.email);
        }
      }
      
      toast.success(`Sent ${sent} invitations`);
      setSelectedIds([]);
      fetchPeople();
    } catch (error) {
      toast.error("Failed to send invites");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Approve coach
  const handleApproveCoach = async (person) => {
    try {
      await axios.post(`${API}/coaches/${person.person_id}/approve`, {}, { headers });
      toast.success(`${person.first_name} ${person.last_name} approved!`);
      // Update local state
      setSelectedPerson(prev => prev ? { ...prev, org_status: "approved" } : null);
      fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve coach");
    }
  };

  // Reject coach
  const handleRejectCoach = async (person) => {
    try {
      await axios.post(`${API}/coaches/${person.person_id}/reject`, {}, { headers });
      toast.success(`${person.first_name} ${person.last_name} rejected`);
      setSelectedPerson(prev => prev ? { ...prev, org_status: "rejected" } : null);
      fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject coach");
    }
  };

  // Open profile panel
  const openProfile = (person) => {
    if (person.person_type === "household") {
      navigate(routeBase.startsWith("/org/") ? `${routeBase}/households/${person.household_id}` : `/admin/households/${person.household_id}`);
      return;
    }
    setSelectedPerson(person);
    setProfileTab("overview");
    if (!["player", "parent"].includes(person.person_type)) fetchProfile(person.person_type, person.person_id);
  };

  // Close profile panel
  const closeProfile = () => {
    setSelectedPerson(null);
    setProfileData(null);
  };

  // Open edit dialog
  const openEdit = (person) => {
    setEditItem(person);
    setEditMode(true);
    
    if (person.person_type === "staff") {
      setStaffForm({
        first_name: person.first_name || "",
        last_name: person.last_name || "",
        email: person.email || "",
        phone: person.phone || "",
        role: person.role || "manager",
        permissions: person.permissions || "limited",
        assigned_programs: person.assigned_programs || []
      });
      setStaffDialogOpen(true);
    } else if (person.person_type === "board") {
      setBoardForm({
        first_name: person.first_name || "",
        last_name: person.last_name || "",
        email: person.email || "",
        phone: person.phone || "",
        position: person.position || "member_at_large",
        title: person.title || "",
        bio: person.bio || "",
        term_start: person.term_start || "",
        term_end: person.term_end || "",
        linkedin_url: person.linkedin_url || "",
        show_on_public_page: person.show_on_public_page !== false,
        has_system_access: person.has_system_access || false,
        send_invite: false,
        permissions: person.permissions || {
          view_financials: false, view_registrations: false, view_programs: false,
          approve_coaches: false, manage_staff: false, manage_settings: false, full_admin: false
        }
      });
      setBoardDialogOpen(true);
    }
    closeProfile();
  };

  // Filter pills data
  const filterPills = [
    { key: "all", label: "All", count: summary.total || 0 },
    { key: "player", label: "Players", count: summary.players || 0 },
    { key: "parent", label: "Parents", count: summary.parents || 0 },
    { key: "household", label: "Households", count: summary.households || 0 },
    { key: "staff", label: "Staff", count: summary.staff || 0 },
    { key: "pending", label: "Pending", count: summary.pending || 0, isStatus: true },
    { key: "issues", label: "Issues", count: summary.issues || 0, isStatus: true },
  ];

  // Handle filter change
  const handleFilterChange = (key, isStatus = false) => {
    if (isStatus) {
      setStatusFilter(key === statusFilter ? "all" : key);
      setActiveFilter("all");
    } else {
      setActiveFilter(key);
      setStatusFilter("all");
    }
    setSelectedIds([]);
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${(firstName || "")[0] || ""}${(lastName || "")[0] || ""}`.toUpperCase() || "?";
  };

  return (
    <PageLayout>
      <div className="people-families space-y-6" data-testid="admin-people-page">
        <EditorialPageHeader
          title="People & Families"
          description="Find people, roles and the households they belong to."
          headingId="people-heading"
          aside={<div className="flex flex-col items-end gap-3">
          {/* Bulk Actions */}
          {selectedIds.length > 0 ? (
            <div className="flex items-center gap-3 bg-slate-100 px-4 py-2 rounded-full">
              <span className="text-sm font-medium text-slate-700">
                {selectedIds.length} selected
              </span>
              <div className="h-4 w-px bg-slate-300" />
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleBulkExport}
                disabled={bulkActionLoading}
                className="h-8"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleBulkSendInvites}
                disabled={bulkActionLoading}
                className="h-8"
              >
                <Send className="w-4 h-4 mr-1" />
                Send Invites
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setSelectedIds([])}
                className="h-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : null}

          <Button onClick={() => setAddPersonOpen(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full" data-testid="add-person-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add a person
          </Button>
          </div>}
        />

        <PeopleWorkspaceNav active="directory" />
        {embedded && <nav className="pf-tabs" aria-label="People workspace">
          <Button variant="secondary" aria-current="page" onClick={() => navigate(`${routeBase}/directory`)}>Directory</Button>
          <Button variant="outline" onClick={() => navigate(`${routeBase}/households`)}>Households</Button>
          <Button variant="outline" onClick={() => navigate(`${routeBase}/enrollments`)}>Enrollment history</Button>
        </nav>}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Saved views</span>
            <button
              type="button"
              onClick={() => { setActiveFilter("all"); setStatusFilter("all"); setSelectedIds([]); }}
              className="rounded-full bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white"
              data-testid="saved-view-all"
            >
              Everyone
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange("issues", true)}
              className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800"
              data-testid="saved-view-attention"
            >
              Needs attention · {summary.issues || 0}
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange("pending", true)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="saved-view-pending"
            >
              Pending · {summary.pending || 0}
            </button>
          </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="people-search"
            />
          </div>
          
          {/* Filter Pills */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
            {filterPills.map(pill => (
              <button
                key={pill.key}
                onClick={() => handleFilterChange(pill.key, pill.isStatus)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  (pill.isStatus ? statusFilter === pill.key : activeFilter === pill.key) ||
                  (pill.isStatus && activeFilter === pill.key)
                    ? "bg-white text-slate-900 shadow-sm" 
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid={`filter-${pill.key}`}
              >
                {pill.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  pill.key === "pending" ? "bg-amber-100 text-amber-700" :
                  pill.key === "issues" ? "bg-red-100 text-red-700" :
                  "bg-slate-200 text-slate-600"
                }`}>
                  {pill.count}
                </span>
              </button>
            ))}
          </div>
          
          <Button variant="outline" size="icon" onClick={fetchPeople} className="rounded-xl" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        </div>

        {/* Pending Invites Banner */}
        {pendingInvites.length > 0 && (activeFilter === "all" || activeFilter === "coach") && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-amber-900">
                Pending Invites ({pendingInvites.length})
              </h3>
            </div>
            <div className="space-y-2">
              {pendingInvites.map((inv) => (
                <div key={inv.invite_id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {inv.first_name} {inv.last_name}
                      </p>
                      <p className="text-sm text-slate-500">{inv.email}</p>
                    </div>
                    {inv.email_sent ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">Email Sent</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 text-xs">Email Failed</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResendInvite(inv.invite_id)}
                      className="text-xs"
                    >
                      <Send className="w-3 h-3 mr-1" /> Resend
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvite(inv.invite_id)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      <X className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* People List */}
        {loadError ? (
          <div className="rounded-2xl border border-red-200 bg-white px-6 py-14 text-center" role="alert">
            <AlertTriangle className="mx-auto h-10 w-10 text-red-500" />
            <h2 className="mt-4 text-lg font-semibold text-slate-950">People could not be loaded</h2>
            <p className="mt-1 text-sm text-slate-500">Your current view is preserved. Check the connection and try again.</p>
            <Button onClick={fetchPeople} className="mt-5 rounded-xl bg-teal-700 hover:bg-teal-800">
              <RefreshCw className="mr-2 h-4 w-4" /> Try again
            </Button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : people.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-2xl border border-slate-200">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-200" />
            <p className="text-slate-500 mb-4">No people found</p>
            <Button variant="outline" onClick={() => { setActiveFilter("all"); setSearchQuery(""); setStatusFilter("all"); }}>
              Clear filters
            </Button>
            
            {/* Debug Info Section */}
            {debugInfo && (
              <div className="mt-6 text-left max-w-lg mx-auto">
                <button 
                  onClick={() => setShowDebug(!showDebug)}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {showDebug ? "Hide" : "Show"} diagnostic info
                </button>
                
                {showDebug && (
                  <div className="mt-3 p-4 bg-slate-50 rounded-lg text-xs font-mono text-left overflow-auto">
                    <p className="font-bold mb-2 text-slate-700">Debug Information:</p>
                    <p><strong>Your org_id:</strong> {debugInfo.user_info?.org_id || "Not set"}</p>
                    <p><strong>Your email:</strong> {debugInfo.user_info?.email}</p>
                    <p><strong>Your role:</strong> {debugInfo.user_info?.role}</p>
                    
                    {debugInfo.organization && (
                      <>
                        <p className="mt-2"><strong>Organization:</strong> {debugInfo.organization?.name}</p>
                        <p><strong>Subdomain:</strong> {debugInfo.organization?.subdomain}</p>
                      </>
                    )}
                    
                    {debugInfo.coach_counts && (
                      <>
                        <p className="mt-2"><strong>Coaches with this org_id:</strong> {debugInfo.coach_counts?.with_this_org_id}</p>
                        <p><strong>Coaches with no org_id:</strong> {debugInfo.coach_counts?.with_no_org_id}</p>
                      </>
                    )}
                    
                    {debugInfo.all_org_ids_in_coaches && (
                      <p className="mt-2"><strong>All org_ids in coaches:</strong> {JSON.stringify(debugInfo.all_org_ids_in_coaches)}</p>
                    )}
                    
                    {debugInfo.error && (
                      <p className="mt-2 text-amber-600"><strong>Note:</strong> {debugInfo.error}</p>
                    )}
                    
                    <p className="mt-4 text-slate-500">
                      Please share this info with support to diagnose why coaches aren't appearing.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {/* Table Header with Select All */}
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-4">
              <Checkbox 
                checked={selectedIds.length === people.length && people.length > 0}
                onCheckedChange={handleSelectAll}
                data-testid="select-all"
                aria-label="Select all directory records"
              />
              <span className="text-sm text-slate-500">
                {people.length} directory {people.length === 1 ? "record" : "records"}
              </span>
            </div>
            
            <table className="pf-table" aria-label="People directory" role="table">
            <thead role="rowgroup"><tr className="pf-directory-head" role="row"><th scope="col" id="pf-directory-0" className="pf-sr-only">Selection</th><th scope="col" id="pf-directory-1">Name</th><th scope="col" id="pf-directory-2">Type</th><th scope="col" id="pf-directory-3">Contact</th><th scope="col" id="pf-directory-4">Household</th></tr></thead>
            <tbody className="divide-y divide-slate-100" role="rowgroup">
              {people.map((person) => {
                const typeConfig = TYPE_CONFIG[person.person_type] || TYPE_CONFIG.staff;
                const statusConfig = STATUS_CONFIG[person.status] || STATUS_CONFIG.active;
                const isSelected = selectedIds.includes(`${person.person_type}-${person.person_id}`);
                return <tr role="row" key={`${person.person_type}-${person.person_id}`} className={`pf-directory-row ${isSelected ? "pf-selected" : ""}`} data-testid={`person-row-${person.person_id}`}>
                  <td role="cell" headers="pf-directory-0" className="pf-select-cell"><Checkbox aria-label={`Select ${person.first_name || person.email || "record"}`} checked={isSelected} onCheckedChange={() => handleSelectPerson(person)} /></td>
                  <td role="cell" headers="pf-directory-1" className="pf-identity"><button type="button" onClick={() => openProfile(person)}>{[person.first_name, person.last_name].filter(Boolean).join(" ") || person.email || "Unnamed record"}</button>
                    {person.status !== "active" && <Badge className={statusConfig.color}>{statusConfig.label}</Badge>}
                    {person.compliance && <small>{person.compliance.is_compliant ? "Compliant" : `${person.compliance.pending || 0} pending · ${person.compliance.expired || 0} expired`}</small>}
                    {person.bg_check_status && <small>Background check: {person.bg_check_status}</small>}
                    {(person.team_count > 0 || person.program_count > 0) && <small>{person.team_count || 0} teams · {person.program_count || 0} programs</small>}
                  </td>
                  <td role="cell" headers="pf-directory-2"><span aria-hidden="true" className="pf-mobile-label">Type</span><span className="pf-type">{typeConfig.label}</span></td>
                  <td role="cell" headers="pf-directory-3"><span aria-hidden="true" className="pf-mobile-label">Contact</span><span>{person.email || "Email not provided"}</span>{person.phone && <small>{person.phone}</small>}</td>
                  <td role="cell" headers="pf-directory-4"><span aria-hidden="true" className="pf-mobile-label">Household</span>{person.household_name || "No household linked"}</td>
                </tr>;
              })}
            </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Profile Sheet */}
      <Sheet open={!!selectedPerson} onOpenChange={(open) => !open && closeProfile()}>
        <SheetContent className="people-families pf-profile w-full sm:max-w-xl overflow-y-auto !bg-white">
          {selectedPerson && (
            <>
              <SheetHeader className="pb-4 border-b">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {selectedPerson.photo_url ? (
                      <img src={selectedPerson.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl font-medium text-slate-600">
                        {getInitials(selectedPerson.first_name, selectedPerson.last_name)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <SheetTitle className="text-xl">
                      {selectedPerson.first_name || selectedPerson.last_name 
                        ? [selectedPerson.first_name, selectedPerson.last_name].filter(Boolean).join(" ")
                        : selectedPerson.email
                      }
                    </SheetTitle>
                    <SheetDescription className="flex items-center gap-2 mt-1">
                      <Badge className={TYPE_CONFIG[selectedPerson.person_type]?.color}>
                        {TYPE_CONFIG[selectedPerson.person_type]?.label}
                      </Badge>
                      {selectedPerson.role && (
                        <span className="text-slate-500 capitalize">{selectedPerson.role.replace(/_/g, " ")}</span>
                      )}
                      {selectedPerson.position && (
                        <span className="text-slate-500 capitalize">{selectedPerson.position.replace(/_/g, " ")}</span>
                      )}
                    </SheetDescription>
                  </div>
                  
                  {/* Actions dropdown */}
                  {!["player", "parent"].includes(selectedPerson.person_type) && <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-lg" aria-label="Profile actions">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {(selectedPerson.person_type === "staff" || selectedPerson.person_type === "board") && (
                        <DropdownMenuItem onClick={() => openEdit(selectedPerson)}>
                          Edit Details
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem className="text-red-600">
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>}
                </div>
                
                {/* Contact info */}
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  {selectedPerson.email && (
                    <a href={`mailto:${selectedPerson.email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900">
                      <Mail className="w-4 h-4" />
                      {selectedPerson.email}
                    </a>
                  )}
                  {selectedPerson.phone && (
                    <a href={`tel:${selectedPerson.phone}`} className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900">
                      <Phone className="w-4 h-4" />
                      {selectedPerson.phone}
                    </a>
                  )}
                </div>
              </SheetHeader>

              {selectedPerson.household_id && <section className="pf-connection">
                <h3>Household connection</h3><p>{selectedPerson.household_name || "Household"}</p>
                <p>Household membership does not establish individual custody or permission to act for a player.</p>
                <Button onClick={() => navigate(routeBase.startsWith("/org/") ? `${routeBase}/households/${selectedPerson.household_id}` : `/admin/households/${selectedPerson.household_id}`)}>View household</Button>
              </section>}
              {/* Tabs */}
              {!["player", "parent"].includes(selectedPerson.person_type) && <Tabs value={profileTab} onValueChange={setProfileTab} className="mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="assignments">Assignments</TabsTrigger>
                  {selectedPerson.person_type === "coach" && (
                    <TabsTrigger value="compliance">Compliance</TabsTrigger>
                  )}
                </TabsList>

                {profileLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <>
                    {/* Overview Tab */}
                    <TabsContent value="overview" className="mt-4 space-y-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <h4 className="font-medium text-slate-900 mb-3">Status</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Account Status</p>
                            <Badge className={STATUS_CONFIG[selectedPerson.status]?.color}>
                              {STATUS_CONFIG[selectedPerson.status]?.label}
                            </Badge>
                          </div>
                          {selectedPerson.person_type === "coach" && (
                            <>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Org Status</p>
                                <Badge className={selectedPerson.org_status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                                  {selectedPerson.org_status || "Pending"}
                                </Badge>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Background Check</p>
                                <Badge className={
                                  selectedPerson.bg_check_status === "clear" ? "bg-green-100 text-green-700" :
                                  selectedPerson.bg_check_status === "pending" ? "bg-amber-100 text-amber-700" :
                                  selectedPerson.bg_check_status === "failed" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-600"
                                }>
                                  {selectedPerson.bg_check_status === "clear" ? "✓ Clear" :
                                   selectedPerson.bg_check_status === "failed" ? "✗ Failed" :
                                   selectedPerson.bg_check_status || "Not started"}
                                </Badge>
                                {selectedPerson.bg_check_status === "clear" && (
                                  <p className="text-xs text-green-600 mt-1">Valid across all orgs</p>
                                )}
                                {(!selectedPerson.bg_check_status || selectedPerson.bg_check_status === "failed") && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-2 text-xs rounded-full"
                                    onClick={async () => {
                                      try {
                                        const headers = { Authorization: `Bearer ${token}` };
                                        await axios.post(`${API}/coaches/${selectedPerson.id}/background-check/request`, {}, { headers });
                                        toast.success("Background check initiated");
                                        fetchPeople();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || "Failed to request background check");
                                      }
                                    }}
                                  >
                                    <Shield className="w-3 h-3 mr-1" />
                                    Request Check
                                  </Button>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Compliance</p>
                                <Badge className={selectedPerson.compliance?.is_compliant ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                                  {selectedPerson.compliance?.is_compliant ? "Compliant" : "Needs attention"}
                                </Badge>
                              </div>
                            </>
                          )}
                          {selectedPerson.person_type === "board" && (
                            <>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Term Start</p>
                                <p className="text-sm text-slate-700">{selectedPerson.term_start || "—"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Term End</p>
                                <p className="text-sm text-slate-700">{selectedPerson.term_end || "—"}</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Approve/Reject for pending coaches */}
                      {selectedPerson.person_type === "coach" && 
                       (selectedPerson.org_status === "pending_approval" || selectedPerson.org_status === "pending") && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <p className="text-sm font-medium text-amber-900">This coach is awaiting approval</p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                              onClick={() => handleApproveCoach(selectedPerson)}
                            >
                              <UserCheck className="w-4 h-4 mr-2" />
                              Approve Coach
                            </Button>
                            <Button 
                              variant="outline"
                              className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                              onClick={() => handleRejectCoach(selectedPerson)}
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-blue-50 rounded-xl">
                          <p className="text-xs text-blue-600 mb-1">Teams</p>
                          <p className="text-2xl font-bold text-blue-700">{selectedPerson.team_count || 0}</p>
                        </div>
                        <div className="p-4 bg-emerald-50 rounded-xl">
                          <p className="text-xs text-emerald-600 mb-1">Programs</p>
                          <p className="text-2xl font-bold text-emerald-700">{selectedPerson.program_count || 0}</p>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button variant="outline" className="flex-1 rounded-xl">
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </Button>
                      </div>
                    </TabsContent>

                    {/* Assignments Tab */}
                    <TabsContent value="assignments" className="mt-4 space-y-4">
                      <div>
                        <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Team Assignments
                        </h4>
                        {(profileData?.team_assignments?.length > 0 || selectedPerson.teams?.length > 0) ? (
                          <div className="space-y-2">
                            {(profileData?.team_assignments || selectedPerson.teams || []).map((team, idx) => (
                              <div key={idx} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-slate-900">{team.team_name}</p>
                                  <p className="text-sm text-slate-500 capitalize">{team.role?.replace(/_/g, " ")}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-xl">
                            Not assigned to any teams
                          </p>
                        )}
                      </div>

                      <div>
                        <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Program Assignments
                        </h4>
                        {(profileData?.program_assignments?.length > 0 || selectedPerson.programs?.length > 0) ? (
                          <div className="space-y-2">
                            {(profileData?.program_assignments || selectedPerson.programs || []).map((program, idx) => (
                              <div key={idx} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-slate-900">{program.program_name}</p>
                                  <p className="text-sm text-slate-500 capitalize">{program.role?.replace(/_/g, " ")}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-xl">
                            Not assigned to any programs
                          </p>
                        )}
                      </div>
                    </TabsContent>

                    {/* Compliance Tab */}
                    {selectedPerson.person_type === "coach" && (
                      <TabsContent value="compliance" className="mt-4 space-y-4">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Compliance Records
                          </h4>
                          {profileData?.compliance_records?.length > 0 ? (
                            <div className="space-y-2">
                              {profileData.compliance_records.map((record, idx) => (
                                <div key={idx} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-slate-900">{record.requirement_name || record.type}</p>
                                    <p className="text-sm text-slate-500">
                                      Expires: {record.expires_at ? new Date(record.expires_at).toLocaleDateString() : "N/A"}
                                    </p>
                                  </div>
                                  <Badge className={
                                    record.status === "approved" || record.status === "verified" ? "bg-green-100 text-green-700" :
                                    record.status === "pending" ? "bg-amber-100 text-amber-700" :
                                    "bg-red-100 text-red-700"
                                  }>
                                    {record.status}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-xl">
                              No compliance records found
                            </p>
                          )}
                        </div>
                      </TabsContent>
                    )}
                  </>
                )}
              </Tabs>}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Dialogs - Using existing components */}
      <Dialog open={addPersonOpen} onOpenChange={setAddPersonOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a person</DialogTitle>
            <DialogDescription>Add their contact details, then choose the organization role for their invitation.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="person-first-name">First name</Label><Input id="person-first-name" value={addPersonForm.first_name} onChange={(event) => setAddPersonForm((current) => ({ ...current, first_name: event.target.value }))} /></div>
            <div className="space-y-2"><Label htmlFor="person-last-name">Last name</Label><Input id="person-last-name" value={addPersonForm.last_name} onChange={(event) => setAddPersonForm((current) => ({ ...current, last_name: event.target.value }))} /></div>
            <div className="space-y-2 sm:col-span-2"><Label htmlFor="person-email">Email</Label><Input id="person-email" type="email" value={addPersonForm.email} onChange={(event) => setAddPersonForm((current) => ({ ...current, email: event.target.value }))} /></div>
            <div className="space-y-2"><Label htmlFor="person-phone">Phone <span className="text-slate-400">optional</span></Label><Input id="person-phone" type="tel" value={addPersonForm.phone} onChange={(event) => setAddPersonForm((current) => ({ ...current, phone: event.target.value }))} /></div>
            <div className="space-y-2"><Label htmlFor="person-role">Role</Label><Select value={addPersonForm.role} onValueChange={(role) => setAddPersonForm((current) => ({ ...current, role }))}><SelectTrigger id="person-role"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="coach">Coach</SelectItem>{STAFF_ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}<SelectItem value="board">Board member</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPersonOpen(false)}>Cancel</Button>
            <Button onClick={handleAddPerson} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add person</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffDialog
        open={staffDialogOpen}
        onOpenChange={setStaffDialogOpen}
        staffForm={staffForm}
        setStaffForm={setStaffForm}
        editMode={editMode}
        saving={saving}
        onSave={handleSaveStaff}
      />

      <BoardMemberDialog
        open={boardDialogOpen}
        onOpenChange={setBoardDialogOpen}
        boardForm={boardForm}
        setBoardForm={setBoardForm}
        editMode={editMode}
        saving={saving}
        onSave={handleSaveBoard}
      />
    </PageLayout>
  );
};

const AdminPeople = ({ embedded = false, routeBase = "/admin/people", initialView }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  if (initialView === "enrollments" || searchParams.get("view") === "enrollments") {
    return (
      <PageLayout>
          <div className="space-y-5" data-testid="people-enrollment-workspace">
            <EditorialPageHeader
              eyebrow="People & families"
              title="Enrollment history"
              description="Search across programs when you need a person or household history. Manage current enrollment from its Program Cockpit."
              headingId="enrollment-history-heading"
            />
          <PeopleWorkspaceNav active="enrollments" />
          <AdminRegistrations embedded />
        </div>
      </PageLayout>
    );
  }
  return <AdminPeopleDirectory embedded={embedded} routeBase={routeBase} />;
};

export default AdminPeople;
